import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { detailsSchemaBase } from '../shared/details'
import { medicalInformationSchema } from '../medical-information'
import { notesSchema } from '../shared/notes'
import { CreatePetFormControls } from './controls'
import { CreatePetFormContent } from './content'
import { useFileUpload } from '$hooks/use-file-upload'
import { DefaultValues } from 'react-hook-form'

export type CreatePetSchema = z.infer<typeof createPetSchema>

const createPetSchema = z.object({
  ...detailsSchemaBase.shape,
  ...medicalInformationSchema.shape,
  ...notesSchema.shape,
})

type CreatePetFormContextType = {
  onSubmit: (values: CreatePetSchema) => void
  onCancel: () => void
  isLoading?: boolean
  fileHandler: ReturnType<typeof useFileUpload>
}

const CreatePetFormContext = createContext<CreatePetFormContextType | undefined>(undefined)

type CreatePetFormProps = CreatePetFormContextType & {
  children?: ReactNode,
  fileHandler: ReturnType<typeof useFileUpload>
  defaultValues?: DefaultValues<CreatePetSchema>
}

export function CreatePetForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  fileHandler,
  defaultValues,
}: CreatePetFormProps) {

  const context: CreatePetFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
    fileHandler,
  }

  return (
    <CreatePetFormContext.Provider value={context}>
      <Form<CreatePetSchema, typeof createPetSchema>
        schema={createPetSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </CreatePetFormContext.Provider>
  )
}

CreatePetForm.Content = CreatePetFormContent
CreatePetForm.Controls = CreatePetFormControls

export function useCreatePetForm() {
  const context = useContext(CreatePetFormContext)

  if (context === undefined) {
    throw new Error('useCreatePetForm must be used within a CreatePetFormProvider')
  }

  return context
}
